import { Dialog, Transition } from '@headlessui/react';
import { Fragment, useMemo } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useLanguage } from 'contexts/LanguageContext';

interface PodTableViewerProps {
  isOpen: boolean;
  onClose: () => void;
  podTableData: string;
}

interface PodData {
  id: string;
  address: string;
  measurementType: string;
  annualConsumption: string;
}

const PodTableViewer = ({ isOpen, onClose, podTableData }: PodTableViewerProps) => {
  const { t } = useLanguage();

  // Parse the XML data to extract POD information in the correct format
  const podItems = useMemo(() => {
    if (!podTableData) return [];

    const pods: PodData[] = [];
    
    try {
      // First, identify the table structure from XML
      // Find table rows (<w:tr>)
      const rowsRegex = /<w:tr[\s\S]*?<\/w:tr>/g;
      const rows = podTableData.match(rowsRegex) || [];
      
      if (rows.length <= 1) return []; // No data rows (only header perhaps)
      
      // Skip the header row (first row)
      const dataRows = rows.slice(1);
      
      // Process each row to extract cells
      dataRows.forEach(row => {
        // Find each cell content in the row
        const cells: string[] = [];
        const cellRegex = /<w:tc>[\s\S]*?<\/w:tc>/g;
        const cellMatches = row.match(cellRegex) || [];
        
        // Extract text from each cell
        cellMatches.forEach(cell => {
          const textRegex = /<w:t>([^<]*)<\/w:t>/g;
          let cellText = '';
          let match;
          
          while ((match = textRegex.exec(cell)) !== null) {
            cellText += match[1];
          }
          
          cells.push(cellText.trim());
        });
        
        // Only add if we have enough cells with data
        if (cells.length >= 4) {
          pods.push({
            id: cells[0] || '',
            address: cells[1] || '',
            measurementType: cells[2] || '',
            annualConsumption: cells[3] || ''
          });
        }
      });
    } catch (error) {
      console.error('Hiba a POD adatok feldolgozása során:', error);
    }
    
    return pods;
  }, [podTableData]);

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/50 dark:bg-black/60" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-4xl transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 p-6 text-left align-middle shadow-xl transition-all">
                <div className="flex justify-between items-center mb-4">
                  <Dialog.Title as="h3" className="text-lg font-medium text-gray-900 dark:text-gray-100">
                    {t('documents.podTableViewer')}
                  </Dialog.Title>
                  <button
                    type="button"
                    className="rounded-md p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none"
                    onClick={onClose}
                  >
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>
                
                <div className="mt-4 max-h-[70vh] overflow-auto">
                  {podItems.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-900/30">
                          <tr>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                              POD
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                              {t('documents.podAddress')}
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                              {t('documents.measurementType')}
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                              {t('documents.annualConsumption')}
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                          {podItems.map((pod, index) => (
                            <tr key={index} className={index % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-900/20'}>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
                                {pod.id}
                              </td>
                              <td className="px-6 py-4 whitespace-normal text-sm text-gray-500 dark:text-gray-400">
                                {pod.address}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                {pod.measurementType}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                {pod.annualConsumption}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="flex flex-col space-y-4">
                      <div className="bg-gray-50 dark:bg-gray-900/30 p-4 rounded-lg border border-gray-200 dark:border-gray-700 text-center">
                        <p className="text-sm text-gray-500 dark:text-gray-400">{t('documents.noPodTableData')}</p>
                      </div>
                      
                      {/* Fallback to raw XML view when parsing fails */}
                      {podTableData && (
                        <div className="mt-4 bg-gray-50 dark:bg-gray-900/30 p-4 rounded-lg border border-gray-200 dark:border-gray-700 max-h-[50vh] overflow-auto">
                          <details>
                            <summary className="text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer pb-2">
                              {t('documents.viewRawData')}
                            </summary>
                            <pre className="text-xs text-gray-600 dark:text-gray-400 whitespace-pre-wrap mt-2">
                              {podTableData}
                            </pre>
                          </details>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="mt-6 flex justify-end">
                  <button
                    type="button"
                    className="inline-flex justify-center rounded-md border border-transparent bg-teal-100 dark:bg-teal-900/30 px-4 py-2 text-sm font-medium text-teal-900 dark:text-teal-300 hover:bg-teal-200 dark:hover:bg-teal-900/50 focus:outline-none"
                    onClick={onClose}
                  >
                    {t('common.close')}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default PodTableViewer; 